/**
 * Step labels come from `stepId`, not from the session's `design` blob.
 *
 * Reading them off `design.nodes[].label` is what forces /complex-sessions to
 * ship its react-flow layout (positions, widths, edge ids) to a screen that
 * never draws a canvas — 1.7 MB of the 3.1 MB response on this stack. The deck
 * maps stepId -> label locally and needs none of it.
 */
export const STEP_LABELS: Record<string, string> = {
	authentication: 'Biometric',
	basicInfo: 'Basic Info',
	kyc: 'KYC',
	kyb: 'KYB',
	kybForm: 'KYB Docs.',
	aml: 'AML',
	accreditation: 'Accreditation',
	signAgreement: 'Sign Doc.',
	dynamicForm: 'Form',
	form: 'Indv. Or Entity',
	proofReading: 'Doc. Review',
	successScreen: 'Success',
	payIn: 'Fund (Pay In)',
	payOut: 'Fund (Pay Out)',
	balanceCheck: 'Balance Check',
	sessionPdf: 'Session PDF',
	event: 'Event',
};

/**
 * The six actions the deck reports on, in the order the design lays them out.
 * `series` is the /onboarding-summary chartData series that backs the sparkline;
 * 506b and 506c share one combined Accreditation series, so their tiles show the
 * trend without claiming a per-tier delta.
 */
export const ACTIONS = [
	{ key: 'kyc', label: '# of KYC', series: 'KYC', tone: 1, exact: true },
	{ key: 'kyb', label: '# of KYB', series: 'KYB', tone: 2, exact: true },
	{ key: 'aml', label: '# of AML', series: 'AML', tone: 3, exact: true },
	{
		key: 'signAgreement',
		label: '# of Sign Agreement',
		series: 'Sign Agreement',
		tone: 4,
		exact: true,
	},
	{
		key: '506b',
		label: '# of Accreditation(506B)',
		series: 'Accreditation',
		tone: 5,
		exact: false,
	},
	{
		key: '506c',
		label: '# of Accreditation(506C)',
		series: 'Accreditation',
		tone: 6,
		exact: false,
	},
] as const;

export const QUEUE_ACTIONS: { key: string; label: string }[] = [
	{ key: 'kyc', label: 'KYC document review' },
	{ key: 'aml', label: 'AML potential matches' },
	{ key: 'kyb', label: 'KYB verification' },
	{ key: 'kybForm', label: 'KYB documents' },
	{ key: '506b', label: 'Accreditation (506B)' },
	{ key: '506c', label: 'Accreditation (506C)' },
	/* The third value accreditationType can take — the tier the investor picks
	   for themselves. It had no bucket in the analytics response, so 113
	   outstanding accreditations were dropped as "unmapped" and the queue's
	   total was short by exactly that many. */
	/* "(choice)" rather than "(investor choice)" so the row is the same width as
	   the two above it — the panel is 260px and the longer wording pushed the
	   number onto a second line. */
	{ key: 'investorChoice', label: 'Accreditation (choice)' },
	{ key: 'signAgreement', label: 'Sign agreement' },
	{ key: 'proofReading', label: 'Document review' },
];

/** Past this many minutes a waiting session is amber; twice it is red. */
export const REVIEW_TARGET_MINUTES = 30;
